/**
 * Zooming an inline vehicle SVG to the artwork inside it.
 *
 * A template's viewBox has margins around the vehicle - the fire truck uses about 84% of the width and 65%
 * of the height of its 1024x580 stage - and those empty pixels cost something different in each place the
 * car is shown. In the studio they are drawing area she does not get, which is why this started life there;
 * on the Lookout's drive badge they are the difference between a car that fills its badge and a small car
 * with a bar of white sky over it.
 *
 * Shared because the two have to agree. The studio bakes her thumbnail from the SVG it has cropped, so the
 * PNG in the save is a window onto template space that nothing records - and the badge, which redraws the
 * car from the template to put the pup in its cab, has to arrive at the same window or the swap from one to
 * the other visibly jumps.
 */

/**
 * Crop `svg` to the bounding box of everything in it, plus a margin. Returns whether it happened.
 *
 * `getBBox` needs the element to be in a laid-out document and is not implemented in jsdom at all, so a
 * false return is an ordinary outcome: the caller keeps the authored viewBox, which is the whole drawing
 * with its margins - correct, just not as large as it could be.
 */
export function cropToArtwork(svg: SVGSVGElement, marginFraction = 0.03): boolean {
  let box: { x: number; y: number; width: number; height: number };
  try {
    box = svg.getBBox();
  } catch {
    return false;
  }
  if (!(box.width > 0) || !(box.height > 0)) return false;

  // getBBox ignores stroke width, so a thick outline would be clipped without a margin.
  const margin = Math.max(box.width, box.height) * marginFraction + 12;
  svg.setAttribute(
    'viewBox',
    `${round(box.x - margin)} ${round(box.y - margin)} ${round(box.width + margin * 2)} ${round(
      box.height + margin * 2,
    )}`,
  );
  return true;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
